import React, { useState } from 'react';
import './FunFact.css';

const FunFact = () => {
  const [funFact, setFunFact] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentTopic, setCurrentTopic] = useState('');
  const [showPopup, setShowPopup] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

   // Parse markdown bold syntax (**text**) and convert to React elements
  const parseMarkdownBold = (text) => {
    if (!text) return text;
    
    // Split text by **text** pattern
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    
    return parts.map((part, index) => {
      // Check if this part is bold text (starts and ends with **)
      if (part.startsWith('**') && part.endsWith('**')) {
        // Remove the ** markers and wrap in <strong>
        const boldText = part.slice(2, -2);
        return <strong key={index}>{boldText}</strong>;
      }
      // Regular text
      return <span key={index}>{part}</span>;
    });
  };


  // Fun fact categories with icons (only 3 categories)
  const funFactCategories = [
    { 
      id: 'favoriteAnimal', 
      label: 'ANIMALS', 
      icon: '🐾',
      gradient: 'linear-gradient(135deg, #FF6B6B, #FF8E53)'
    },
    { 
      id: 'favoriteSnack', 
      label: 'SNACKS', 
      icon: '🍿',
      gradient: 'linear-gradient(135deg, #4ECDC4, #44A08D)'
    },
    { 
      id: 'favoriteRelaxation', 
      label: 'RELAXATION', 
      icon: '🧘',
      gradient: 'linear-gradient(135deg, #667eea, #764ba2)'
    }
  ];

  // Get user preferences from localStorage
  const getUserPreferences = () => {
    const preferences = {};
    
    // Try to get from student profile data
    const studentData = localStorage.getItem('studentProfileData');
    if (studentData) {
      try {
        const parsed = JSON.parse(studentData);
        if (parsed.favoriteAnimal) preferences.favoriteAnimal = parsed.favoriteAnimal;
        if (parsed.favoriteSnack) preferences.favoriteSnack = parsed.favoriteSnack;
        if (parsed.favoriteRelax) preferences.relaxationMethod = parsed.favoriteRelax;
        if (parsed.grade) preferences.grade = parsed.grade;
      } catch (e) {
        console.log('Could not parse student profile data');
      }
    }

    // Fallback to individual localStorage items
    if (!preferences.favoriteAnimal) {
      preferences.favoriteAnimal = localStorage.getItem('favoriteAnimal');
    }
    if (!preferences.favoriteSnack) {
      preferences.favoriteSnack = localStorage.getItem('favoriteSnack');
    }
    if (!preferences.relaxationMethod) {
      preferences.relaxationMethod = localStorage.getItem('favoriteRelax');
    }
    if (!preferences.grade) {
      preferences.grade = localStorage.getItem('currentGrade');
    }

    return preferences;
  };

  const generateFunFact = async (topic) => {
    setLoading(true);
    setError('');
    setCurrentTopic(topic);
    setFunFact('');

    try {
      const userPreferences = getUserPreferences();
      // Try multiple sources for user ID
      const userId = localStorage.getItem('currentUserId') || 
                     localStorage.getItem('signupUserId') || 
                     'e22bc299-8c6c-41a0-b818-ea7075a349f8'; // Fallback to your actual user ID
      
      console.log('🔍 FunFact Debug:', {
        topic,
        userPreferences,
        userId,
        relaxationMethod: userPreferences.relaxationMethod,
        favoriteSnack: userPreferences.favoriteSnack,
        localStorageData: localStorage.getItem('studentProfileData')
      });
      
      const response = await fetch('/funfact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          topic: topic,
          user_preferences: userPreferences,
          user_id: userId
        })
      });

      if (!response.ok) {
        // Backend returns 503 with a { success:false, fun_fact } body on failure.
        let message = 'Unable to fetch fun facts at this time. Please try again later.';
        try {
          const errData = await response.json();
          if (errData && errData.fun_fact) message = errData.fun_fact;
        } catch (_) { /* non-JSON error body; use default message */ }
        throw new Error(message);
      }

      const data = await response.json();

      // Defensive: API may also signal failure in a 200 body.
      if (data.success === false) {
        setError(data.fun_fact || 'Unable to fetch fun facts at this time. Please try again later.');
        setFunFact('');
        setShowPopup(true);
        return;
      }

      setFunFact(data.fun_fact);

      // Show the fact with confetti once it has arrived.
      setShowPopup(true);
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 2000);
      
    } catch (err) {
      console.error('Error generating fun fact:', err);
      setError(err.message || 'Unable to fetch fun facts at this time. Please try again later.');
      setFunFact('');
      setShowPopup(true);
    } finally {
      setLoading(false);
    }
  };

  // Confetti component
  const Confetti = () => (
    <div className="confetti">
      {[...Array(18)].map((_, i) => (
        <span key={i} className={`confetti-piece confetti-piece-${i % 6}`}></span>
      ))}
    </div>
  );

  return (
    <div className="funfact-container">
      <div className="funfact-header">
        <h2 className="funfact-title">FUN FACTS</h2>
      </div>

      <div className="funfact-categories">
        {funFactCategories.map((category) => (
          <div key={category.id} className="funfact-category-item">
            <button
              className="funfact-category-btn"
              style={{ background: category.gradient }}
              onClick={() => generateFunFact(category.label)}
              disabled={loading}
            >
              <div className="funfact-category-icon">
                {loading && currentTopic === category.label ? (
                  <div className="funfact-spinner funfact-spinner-btn"></div>
                ) : (
                  category.icon
                )}
              </div>
            </button>
            <div className="funfact-category-label">
              {category.label}
            </div>
          </div>
        ))}
      </div>
      
      {/* Popup Modal */}
      {showPopup && (
        <>
          {showConfetti && <Confetti />}
          <div className="funfact-popup-overlay" onClick={() => setShowPopup(false)}>
            <div className="funfact-popup" onClick={e => e.stopPropagation()}>
              <button className="funfact-popup-close" onClick={() => setShowPopup(false)}>&times;</button>
              <div className="funfact-popup-content">
                <h3 className="funfact-popup-title">
                  {error ? 'Oops!' : `🎉 Fun Fact About ${currentTopic}!`}
                </h3>
                {error ? (
                  <p className="funfact-popup-error">{error}</p>
                ) : (
                  <p className="funfact-popup-text">{parseMarkdownBold(funFact)}</p>
                )}
                <div className="funfact-popup-actions">
                  <button 
                    className="funfact-popup-btn funfact-popup-btn-secondary app-btn"
                    onClick={() => setShowPopup(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default FunFact;
